import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import "./report_selector.scss";

import { ReportEdge } from "../../generated/types";
import { ChevronRightIcon } from "../../assets/icons/chevron_right_icon";
import { AngleRightIcon } from "../../assets/icons/angle_right_icon";
import { Separator } from "../separator/separator";
import { reportsListRender } from "../../utils/reports_list_render";

interface ReportGridProps {
    reports: Array<ReportEdge>;
    selectedReportType: string;
    selectedReportYear: string;
}

export const ReportSelector = (props: ReportGridProps): React.ReactElement => {
    const { reports } = props;
    const [isReportVisible, setIsReportVisible] = useState(false);
    const reportSelectorRef = useRef<any>(null); // Create a ref for the component

    const fsciReports = reports.filter((report) => report?.node?.type === "fsci");
    const ffiReports = reports.filter((report) => report?.node?.type === "ffi");

    // Toggle reports visibility
    const toggleReports = () => {
        setIsReportVisible(!isReportVisible);
    };

    // Click outside handler
    const handleClickOutside = (event: Event) => {
        if (reportSelectorRef.current && !reportSelectorRef.current.contains(event.target)) {
            setIsReportVisible(false);
        }
    };

    // Event listeners for click outside
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const renderReports = (reports: Array<ReportEdge>) => {
        return reportsListRender(
            reports,
            (report: ReportEdge): React.ReactElement => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const node = report.node!;
                return (
                    <li key={node.id}>
                        <NavLink
                            activeClassName="active-link"
                            className="report-view"
                            onClick={() => setIsReportVisible(false)} // Hide selector on report selection
                            to={`/reports/${node.type}/${node.year}/overview`}
                        >
                            {node.year} <ChevronRightIcon />
                        </NavLink>
                    </li>
                );
            },
            (report: ReportEdge): React.ReactElement => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const node = report.node!;
                return (
                    <li className="view-all-reports" key="view-all-reports">
                        <NavLink
                            activeClassName="active-link"
                            className="report-view"
                            onClick={() => setIsReportVisible(false)} // Hide selector on report selection
                            to={`/reports/${node.type}/overview`}
                        >
                            All-Years View <ChevronRightIcon />
                        </NavLink>
                    </li>
                );
            },
        );
    };

    return (
        <div className="report-selector" ref={reportSelectorRef}>
            <button
                aria-controls="report-sections"
                aria-expanded={isReportVisible}
                className="toggle-section"
                onClick={toggleReports}
            >
                <AngleRightIcon />{" "}
                {props.selectedReportYear === "0"
                    ? `${props.selectedReportType.toUpperCase()} All-Years View`
                    : `${props.selectedReportType.toUpperCase()} ${props.selectedReportYear}`}
            </button>
            <div className={`report-sections ${isReportVisible ? "is-visible" : ""}`} id="report-sections">
                <div className="report-section">
                    <h2 id="fsci-reports">Flourishing School Culture Instrument Reports</h2>
                    <ul className="reports reports-fsci" role="listbox">
                        {renderReports(fsciReports)}{" "}
                    </ul>
                </div>
                {ffiReports && ffiReports.length > 0 && (
                    <>
                        <Separator />
                        <div className="report-section">
                            <h2 id="ffi-reports">Flourishing Faith Index</h2>
                            <ul className="reports reports-ffi" role="listbox">
                                {renderReports(ffiReports)}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
